import React, { useMemo, useRef, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import { Box, useTheme, Typography, Tooltip } from "@mui/material";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import { stateToMarkdown } from "draft-js-export-markdown";
import { ReturnDownIcon } from "assets/icons/ReturnDownIcon";
import { ContentState, convertFromHTML, EditorState, Modifier } from "draft-js";
import { palette } from "@mui/system";

const EditorBox = ({
  isFocused,
  setIsFocused,
  handleSave,
  editorState,
  setEditorState,
  summaryEditor,
  saveNote,
  handleBlur,
  isBlur = false,
  onEnterSave = true,
  showSaveIcon = false,
  shouldSave = false,
  placeholder = "",
  isSaving,
  handleContentChange,
  showReturnIcon = false,
}) => {
  const theme = useTheme();
  const [cursor, setCursor] = useState(true);
  const editorRef = useRef(null);

  function onEditorStateChange(newEditorState) {
    if (newEditorState !== editorState) {
      setEditorState(newEditorState);
    }
  }

  async function handleClick() {
    const contentState = editorState.getCurrentContent();
    const markdown = stateToMarkdown(contentState);
    await handleSave(markdown);
  }

  const toolbar = {
    options: ["emoji", "inline", "blockType", "fontSize", "list", "history"],
    inline: {
      options: ["bold", "italic"],
    },
    blockType: {
      options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6"],
    },
    list: {
      options: ["unordered", "ordered"],
    },
  };

  async function handleKeyDown(event) {
    if (event === "split-block" && onEnterSave) {
      await handleClick();
      handleBlur();
    }
  }

  async function handleReturnKey() {
    await handleClick();
    handleBlur();
  }

  // async function handleSaveButton() {
  //   await handleClick(true);
  //   handleBlur();
  // };

  function preventBlur(event) {
    event.preventDefault();
  }

  async function handleEditorBlur() {
    if (saveNote) {
      await handleClick();
    }
    if (isBlur) {
      handleBlur();
    }
  }

  function setEditorReference(ref) {
    if (!cursor) return;
    if (ref) {
      editorRef.current = ref;
      if (!showSaveIcon) {
        editorRef.current.focus();
        setCursor(false);
      }
    }
  }

  function onHandleContentChange(rawDraftContentState) {
    if (typeof handleContentChange == "function") {
      handleContentChange(rawDraftContentState);
    }
  }

  function handlePastedText(text, html, editorState) {
    if (html) {
      let sanitizedHtml = html
        .replace(/<img[^>]*>/gi, "")
        .replace(/<\/?figure[^>]*>/gi, "");

      const { contentBlocks, entityMap } = convertFromHTML(sanitizedHtml);
      const pastedContent = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap
      );

      let contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      contentState = Modifier.replaceWithFragment(
        contentState,
        selection,
        pastedContent.getBlockMap()
      );

      const newEditorState = EditorState.push(
        editorState,
        contentState,
        "insert-fragment"
      );
      setEditorState(newEditorState);
    }
  }

  const editorStyles = useMemo(
    () => ({
      ".wrapper-class": {
        position: "relative",
        padding: "0.5rem",
        border: "1px solid #ccc",
        borderRadius: "15px",
      },
      ".editor-class": {
        minHeight: "72px",
        overflow: "auto",
        maxHeight: "72px",
        backgroundColor: "transparent",
        marginBottom: "2rem",
        padding: "0.5rem",
        paddingBottom: "1rem",
        height: {xs: "25vh", md: "18vh", lg: "22vh"},
        maxHeight: "unset",
        color: theme.palette.text.primary,
        "&.height-50": {
          maxHeight: "unset",
          height: "58vh",
          overflowY: "auto",
        },
        "& ul": {
          listStyleType: "disc",
          paddingLeft: "20px",
          margin: "0 0 16px 0",
        },
  
        "& ul li": {
          display: "list-item",
          marginBottom: "8px",
          listStyleType: "disc",
          listStylePosition: "outside",
          "& > div": {
            display: "inline",
            margin: 0,
          },
        },  
        "& ol": {
          paddingLeft: "20px",
          margin: "0 0 16px 0",
          "& li": {
            display: "block",
            marginBottom: "8px",
            listStyleType: "decimal",
            listStylePosition: "outside",
          },
        },
      },
      ".toolbar-class": {
        background: "transparent",
        border: "none",
        transition: "all 0.3s ease",
        opacity: 1,
        "& .rdw-option-wrapper, & .rdw-dropdown-wrapper": {
          backgroundColor: "transparent",
          color: theme.palette.text.primary,
          border: "none",
          "&:hover, &.rdw-option-active": {
            boxShadow: "none",
          },
          "&:hover": {
            backgroundColor: theme.palette.secondary.dark,
          },
          "&:not(:hover).rdw-option-active": {
            backgroundColor: theme.palette.secondary.main,
          },
          "& .rdw-dropdown-optionwrapper": {
            backgroundColor: theme.palette.background.default,
            border: "none",
            "&:hover": {
              boxShadow: "none",
            },
            "& .rdw-dropdownoption-default:hover": {
              backgroundColor: theme.palette.secondary.dark,
            },
            "& .rdw-dropdownoption-default:not(:hover).rdw-dropdownoption-active":
              {
                backgroundColor: theme.palette.secondary.main,
              },
          },
        },
      },
      ".wrapper-class-noborder": {
        borderRadius: "4px",
      },
      ".toolbar-hidden": {
        opacity: 0,
        height: 0,
        margin: 0,
        visibility: "hidden",
        pointerEvents: "none",
      },
      ".toolbar-visible": {
        opacity: 1,
        zIndex: 1,
      },
    }),
    [theme]
  );
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        height: summaryEditor ? "60%" : "20%",
        backgroundColor: theme.palette.background.main,
        borderRadius: "15px",
        ...editorStyles,
      }}
      pb={0.5}
    >
      {editorState != null && (
        <Editor
          editorStyle={{ textAlign: "left" }}
          toolbarHidden={!isFocused}
          editorRef={setEditorReference}
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          toolbar={toolbar}
          toolbarCustomButtons={
            showSaveIcon
              ? [
                  <Typography
                    variant="caption"
                    key="saveIcon"
                    component="div"
                    color="text.disabled"
                    // onClick={handleSaveButton}
                    // disabled={shouldSave || isSaving}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginLeft: 2,
                      marginBottom: "6px",
                      lineHeight: 1,
                      fontSize: "inherit",
                    }}
                  >
                    {shouldSave ? "unsaved" : isSaving ? "saving" : "saved"}
                  </Typography>,
                ]
              : undefined
          }
          wrapperClassName="wrapper-class"
          editorClassName={`${
            summaryEditor ? "editor-class height-50" : "editor-class"
          }`}
          toolbarClassName={`toolbar-class`}
          placeholder={placeholder ? placeholder : "Add a note..."}
          onKeyDown={handleKeyDown}
          handleKeyCommand={handleKeyDown}
          onBlur={handleEditorBlur}
          handlePastedText={(text, html) =>
            handlePastedText(text, html, editorState)
          }
          onContentStateChange={onHandleContentChange}
        />
      )}
      {!showSaveIcon && (
        <React.Fragment>
          {showReturnIcon && (
          <Tooltip title="Click this or press Enter to save" arrow>
            <ReturnDownIcon
              onMouseDown={preventBlur}
              onClick={handleReturnKey}
              style={{
                width: 18,
                height: 22,
                position: "absolute",
                right: 20,
                bottom: 10,
                cursor: "pointer",
              }}
            />
          </Tooltip>
          )}
          <FormatColorTextIcon
            onMouseDown={preventBlur}
            onClick={() => setIsFocused(!isFocused)}
            style={{
              width: 18,
              height: 22,
              position: "absolute",
              right: showReturnIcon ? 45 : 20,
              bottom: 10,
              cursor: "pointer",
            }}
          />
        </React.Fragment>
      )}
    </Box>
  );
};

export default EditorBox;
